import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Copy, Check, CreditCard, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { createDepositRequest } from '../services/transactionService'
import { mercadoPagoService } from '@/services/mercadopagoService'

const depositSchema = z.object({
  amount: z.number().min(1000, 'Mínimo $1,000').max(1000000, 'Máximo $1,000,000'),
  paymentMethod: z.string().min(1, 'Selecciona un método de pago'),
  reference: z.string().min(4, 'Ingresa el número de referencia'),
})

type DepositFormData = z.infer<typeof depositSchema>

const PAYMENT_METHODS = [
  { id: 'mercadopago', name: 'Mercado Pago', alias: 'casino.mp@email.com' },
  { id: 'transferencia', name: 'Transferencia Bancaria', alias: 'CBU: 0000003100012345678901' },
  { id: 'crypto', name: 'Criptomonedas', alias: 'BTC: 1A2b3C4d5E6f7G8h9I0j' },
]

interface DepositModalProps {
  isOpen: boolean
  onClose: () => void
}

export function DepositModal({ isOpen, onClose }: DepositModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [isMpLoading, setIsMpLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [, setSelectedMethod] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [mpEnabled, setMpEnabled] = useState(false)

  // Initialize MercadoPago
  useEffect(() => {
    mercadoPagoService.init().then(() => {
      setMpEnabled(mercadoPagoService.isEnabled())
    })
  }, [])

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<DepositFormData>({
    resolver: zodResolver(depositSchema),
  })

  const paymentMethod = watch('paymentMethod')
  const currentMethod = PAYMENT_METHODS.find((m) => m.id === paymentMethod)

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleMercadoPago = async (amount: number) => {
    if (!user?.uid || !user?.email) return

    setIsMpLoading(true)
    setError(null)

    const result = await mercadoPagoService.openCheckout(
      amount,
      user.uid,
      user.email
    )

    if (result.success) {
      setSuccess(true)
    } else {
      setError(result.error || t('transactions.depositError'))
    }

    setIsMpLoading(false)
  }

  const onSubmit = async (data: DepositFormData) => {
    if (!user) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await createDepositRequest(
        user.uid,
        data.amount,
        data.paymentMethod,
        data.reference
      )

      if (result.success) {
        setSuccess(true)
      } else {
        setError(result.error || 'Error al procesar el depósito')
      }
    } catch {
      setError('Error al procesar el depósito')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    reset()
    setError(null)
    setSuccess(false)
    setSelectedMethod(null)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="bg-casino-black-light border-casino-gold/30 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-2xl text-casino-gold text-center">
            Depositar
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6"
          >
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-xl text-casino-gold mb-2">¡Solicitud Enviada!</h3>
            <p className="text-gray-400 mb-6">
              Tu depósito está siendo procesado. Te notificaremos cuando se acredite.
            </p>
            <Button variant="gold" onClick={handleClose}>
              Entendido
            </Button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-red-900/30 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg text-sm"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-2">
              <Label className="text-casino-gold/80">Método de Pago</Label>
              <Select
                onValueChange={(value) => {
                  setValue('paymentMethod', value)
                  setSelectedMethod(value)
                }}
              >
                <SelectTrigger className="bg-casino-black border-casino-gold/30">
                  <SelectValue placeholder="Selecciona un método" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method.id} value={method.id}>
                      {method.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.paymentMethod && (
                <p className="text-red-400 text-sm">{errors.paymentMethod.message}</p>
              )}
            </div>

            {currentMethod && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-casino-black/50 rounded-lg p-4"
              >
                {/* MercadoPago automatic checkout option */}
                {paymentMethod === 'mercadopago' && mpEnabled && (
                  <div className="mb-4 pb-4 border-b border-zinc-700">
                    <p className="text-sm text-gray-400 mb-3">{t('transactions.automaticPayment')}</p>
                    <Button
                      type="button"
                      variant="gold"
                      className="w-full"
                      onClick={() => {
                        const amount = watch('amount')
                        if (amount >= 1000) {
                          handleMercadoPago(amount)
                        } else {
                          setError(t('transactions.minAmount'))
                        }
                      }}
                      disabled={isMpLoading}
                    >
                      {isMpLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      {t('transactions.payWithMercadoPago')}
                    </Button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      {t('transactions.redirectToMercadoPago')}
                    </p>
                  </div>
                )}

                <p className="text-sm text-gray-400 mb-2">
                  {paymentMethod === 'mercadopago' && mpEnabled
                    ? t('transactions.orManualPayment')
                    : t('transactions.sendPaymentTo')}
                </p>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-casino-gold text-sm break-all">
                    {currentMethod.alias}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(currentMethod.alias)}
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </motion.div>
            )}

            <div className="space-y-2">
              <Label htmlFor="amount" className="text-casino-gold/80">
                Monto a depositar
              </Label>
              <Input
                id="amount"
                type="number"
                placeholder="10000"
                {...register('amount', { valueAsNumber: true })}
                className="bg-casino-black border-casino-gold/30 focus:border-casino-gold"
              />
              {errors.amount && (
                <p className="text-red-400 text-sm">{errors.amount.message}</p>
              )}
              <div className="flex gap-2 mt-2">
                {[5000, 10000, 50000, 100000].map((amount) => (
                  <Button
                    key={amount}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setValue('amount', amount)}
                    className="flex-1 text-xs"
                  >
                    ${amount.toLocaleString()}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reference" className="text-casino-gold/80">
                Número de Referencia / Comprobante
              </Label>
              <Input
                id="reference"
                type="text"
                placeholder="Ej: 123456789"
                {...register('reference')}
                className="bg-casino-black border-casino-gold/30 focus:border-casino-gold"
              />
              {errors.reference && (
                <p className="text-red-400 text-sm">{errors.reference.message}</p>
              )}
            </div>

            <Button
              type="submit"
              variant="gold"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Procesando...' : 'SOLICITAR DEPÓSITO'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
